import R from 'ramda'
import { mapSuccessWidgetActionsToFormActions } from './actions'
import { mapWidgetTriggersToFormTriggers } from './triggers'
import {
  greaterThan55,
  isNotMultipleOf5,
  isNotNumber,
  lessThan5,
  getCurrentDateTime
} from './common-functions'

/**
 * Takes a widget as we expect to receive it from the backend (after the widgetData has been JSON parsed of course)
 * and applies transformations to turn it into values that the form can understand
 */
const mapTriggersActionsToForm = R.map(
  R.applySpec({
    platform: R.prop('platform'),
    description: R.prop('description'),
    failure: R.prop('failure'),
    success: mapSuccessWidgetActionsToFormActions,
    triggers: mapWidgetTriggersToFormTriggers,
    operator: R.always('all')
  })
)

const getStartDate = R.pathOr(getCurrentDateTime, ['widgetData', 'startDate'])

const getEndDate = R.pathOr(getCurrentDateTime, ['widgetData', 'endDate'])

const hasModerationObject = R.pathOr(false, ['widgetData', 'moderation'])

const isModerationEnabled = R.ifElse(
  hasModerationObject,
  R.pathOr(false, ['widgetData', 'moderation', 'enabled']),
  R.pathOr(false, ['widgetData', 'moderated'])
)

const isModerationAutomated = R.ifElse(
  hasModerationObject,
  R.pathOr(false, ['widgetData', 'moderation', 'automated']),
  R.pathOr(false, ['widgetData', 'automated'])
)

const moderationThreshold = R.pipe(
  R.ifElse(
    hasModerationObject,
    R.pathOr(false, ['widgetData', 'moderation', 'threshold']),
    R.pathOr(false, ['widgetData', 'threshold'])
  ),
  value => Math.round(value * 100).toString()
)

const moderationModerators = R.ifElse(
  hasModerationObject,
  R.pathOr([], ['widgetData', 'moderation', 'moderators']),
  R.pathOr([], ['widgetData', 'moderators'])
)

const RemovedTriggerTypes = [
  'NUMBER_OF_FOLLOWERS',
  'AGE_OF_USER_ACCOUNT',
  'IS_REPLY',
  'IS_RETWEET'
]

const isNotRemovedTriggerType = R.complement(
  R.pipe(R.prop('type'), R.contains(R.__, RemovedTriggerTypes))
)

const filterRelevantTriggers = R.map(
  R.evolve({
    triggers: R.filter(isNotRemovedTriggerType)
  })
)

const getActions = R.pipe(
  R.path(['widgetData', 'actions']),
  filterRelevantTriggers,
  mapTriggersActionsToForm
)

const getDuplicationFromWidget = R.pipe(
  R.prop('duplication'),
  R.cond([
    [isNotNumber, R.always(0)],
    [lessThan5, R.always(0)],
    [greaterThan55, R.always(55)],
    [isNotMultipleOf5, R.always(0)],
    [R.T, R.identity]
  ])
)

const parseWidgetMosaicConfig = mosaic => {
  if (!mosaic) return

  const duplication = getDuplicationFromWidget(mosaic)
  const duplicatesAllowed = Boolean(duplication)
  const { campaignId, mediaId } = mosaic

  return {
    duplicatesAllowed,
    campaignId,
    duplication,
    mediaId: [mediaId]
  }
}

const getMosaic = R.pipe(
  R.pathOr(undefined, ['widgetData', 'mosaic']),
  parseWidgetMosaicConfig
)

const parseWidgetScoreboardConfig = scoreboardConfig => {
  if (!scoreboardConfig) return

  const {
    adsAccountId,
    cardId,
    framePublicId,
    slot,
    pollIntervalMs,
    textFont,
    textColor,
    textSize,
    textGravity,
    textX,
    textY
  } = scoreboardConfig

  return {
    adsAccountId,
    cardId,
    framePublicId,
    slot: Number(slot) || 0,
    pollIntervalMs: Number(pollIntervalMs) || 0,
    // show the stored value, or empty when unset (0 offset is preserved)
    textFont: textFont == null ? '' : textFont,
    textColor: textColor == null ? '' : textColor,
    textSize: textSize == null ? '' : textSize,
    textGravity: textGravity == null ? '' : textGravity,
    textX: textX == null ? '' : textX,
    textY: textY == null ? '' : textY
  }
}

const getScoreboardConfig = R.pipe(
  R.pathOr(undefined, ['widgetData', 'scoreboardConfig']),
  parseWidgetScoreboardConfig
)

const getFilterLists = R.pipe(
  R.path(['widgetData', 'filterLists']),
  R.cond([
    [Array.isArray, R.identity],
    [R.T, R.always([])]
  ])
)

const hasDraftActions = widget =>
  widget.widgetData && widget.widgetData.draftActions

const setDraftActions = widget =>
  hasDraftActions(widget)
    ? R.pipe(
        R.path(['widgetData', 'draftActions']),
        filterRelevantTriggers,
        mapTriggersActionsToForm
      )(widget)
    : []

const widgetToForm = R.applySpec({
  ownerId: R.prop('ownerId'),
  tenantId: R.prop('tenantId'),
  status: R.path(['widgetData', 'status']),
  name: R.path(['widgetData', 'name']),
  description: R.path(['widgetData', 'description']),
  type: R.path(['widgetData', 'type']),
  actions: getActions,
  draftActions: setDraftActions,
  startDate: getStartDate,
  endDate: getEndDate,
  // moderation values
  moderated: isModerationEnabled,
  automated: isModerationAutomated,
  threshold: moderationThreshold,
  moderators: { handles: moderationModerators },
  threadVentureExport: R.pipe(
    R.pathOr([], ['widgetData', 'threadVentureExport']),
    JSON.stringify
  ),
  processedTweets: R.pathOr([], ['widgetData', 'processedTweets']),
  mosaic: getMosaic,
  scoreboardConfig: getScoreboardConfig,
  intentBased: R.path(['widgetData', 'intentBased']),
  hideInteractionWhenFlagged: R.pathOr(false, [
    'widgetData',
    'hideInteractionWhenFlagged'
  ]),
  enableFiltering: R.pathOr(false, ['widgetData', 'enableFiltering']),
  enableBlastFiltering: R.pathOr(true, ['widgetData', 'enableBlastFiltering']),
  notificationFrequency: R.path(['widgetData', 'notificationFrequency']),
  filterLists: getFilterLists,
  timezone: R.path(['widgetData', 'timezone']),
  codeList: R.path(['widgetData', 'codeList']),
  updatedAt: R.prop('updatedAt')
})

export {
  widgetToForm,
  parseWidgetMosaicConfig,
  parseWidgetScoreboardConfig,
  mapTriggersActionsToForm
}



// WEBPACK FOOTER //
// ./src/pages/widgets/mapping/widget-to-form.js